/**
 * The set of language codes recognized by the app's translation system.
 * Mirrors SUPPORTED_LANGS in @paintscout/ui — defined here separately to avoid
 * a circular package dependency (@paintscout/util must not depend on @paintscout/ui).
 */
export type FormattingLang = 'en' | 'es' | 'fr'

/** Runtime list of recognized formatting languages — keep in sync with FormattingLang. */
export const FORMATTING_LANGS: readonly FormattingLang[] = ['en', 'es', 'fr']

/** Default language used when no lang is specified at a call site. */
export const DEFAULT_LANG: FormattingLang = 'en'

/**
 * Narrow a (typically user-supplied) string to a recognized FormattingLang.
 * Returns undefined for anything that isn't a supported language so callers
 * can validate untrusted input at the entry point and fall back to the
 * formatting layer's default rather than threading raw strings down the
 * rendering pipeline.
 */
export function normalizeFormattingLang(lang?: string): FormattingLang | undefined {
  return lang && (FORMATTING_LANGS as readonly string[]).includes(lang) ? (lang as FormattingLang) : undefined
}

/**
 * Maps the app's language codes to BCP 47 locale tags understood by
 * Intl.NumberFormat.  Falls back to 'en-US' for any unrecognized code.
 *
 * Note: French (fr-FR) and Spanish (es-ES) use a thin non-breaking space
 * (\u202f) as the thousands separator and a comma as the decimal separator.
 * Downstream code that compares, splits, or regex-matches these formatted
 * strings must account for \u202f rather than a plain space.
 */
const LANG_TO_LOCALE: Record<FormattingLang, string> = {
  en: 'en-US',
  es: 'es-419',
  fr: 'fr-FR'
}

export function langToLocale(lang: FormattingLang | string): string {
  return (LANG_TO_LOCALE as Record<string, string>)[lang] ?? 'en-US'
}

/** Memoized Intl.NumberFormat instances keyed by "<locale>|<serialized-options>". */
const formatterCache = new Map<string, Intl.NumberFormat>()

function getFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = formatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options)
    formatterCache.set(key, formatter)
  }
  return formatter
}

/**
 * Format a number using the locale that corresponds to the given app language
 * code.  Any additional Intl.NumberFormatOptions are forwarded verbatim, so
 * callers can control decimal places, style, etc.
 *
 * Intl.NumberFormat instances are cached so repeated calls with the same
 * locale + options do not reconstruct the formatter.
 *
 * Examples:
 *   formatNumber(12.5, { lang: 'en' })          → "12.5"
 *   formatNumber(12.5, { lang: 'fr' })          → "12,5"
 *   formatNumber(1234.5, { lang: 'fr' })        -> "1\u202f234,5"  (thin-NBSP thousands sep)
 *   formatNumber(50.1, { lang: 'es', minimumFractionDigits: 1 }) → "50,1"
 */
export function formatNumber(
  value: number,
  { lang = DEFAULT_LANG, ...numberFormatOptions }: { lang?: FormattingLang | string } & Intl.NumberFormatOptions = {}
): string {
  const locale = langToLocale(lang)
  return getFormatter(locale, numberFormatOptions).format(value)
}

/** Separators per locale, read once from Intl. */
const separatorCache = new Map<string, { decimal: string; group: string }>()

/**
 * The decimal and thousands separators for an app language, read from Intl so they always match
 * what formatNumber produces (fr -> "," and U+202F; en and es-419 -> "." and ","). Unknown
 * languages get English separators, like formatNumber.
 */
export function getNumberSeparators(lang?: FormattingLang | string): { decimal: string; group: string } {
  const locale = langToLocale(lang ?? DEFAULT_LANG)
  let separators = separatorCache.get(locale)
  if (!separators) {
    const parts = getFormatter(locale, {}).formatToParts(12_345.5)
    separators = {
      decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
      group: parts.find((part) => part.type === 'group')?.value ?? ','
    }
    separatorCache.set(locale, separators)
  }
  return separators
}

/**
 * Swap the "." in a plain, ungrouped number string ("8.5", from String() or toFixed()) for the
 * language's decimal separator. Digits and precision stay exactly as given, so English output is
 * unchanged. Do not pass a grouped string such as "1,234.5": only the first "." is replaced and
 * the "," is left alone.
 *
 *   localizeDecimal('8.5', 'fr') -> "8,5"
 *   localizeDecimal('8.5', 'en') -> "8.5"
 */
export function localizeDecimal(value: string, lang?: FormattingLang | string): string {
  const { decimal } = getNumberSeparators(lang)
  return decimal === '.' ? value : value.replace('.', decimal)
}

/**
 * Swap every "." between two digits for the language's decimal separator, for a short string
 * that holds several plain numbers, such as a work-order area's dimensions ("20.3'x12.5'x9'").
 * Like localizeDecimal, it takes ungrouped numbers only.
 *
 *   localizeDecimals("20.3'x12.5'x9'", 'fr') -> "20,3'x12,5'x9'"
 */
export function localizeDecimals(value: string, lang?: FormattingLang | string): string {
  const { decimal } = getNumberSeparators(lang)
  return decimal === '.' ? value : value.replaceAll(/(\d)\.(?=\d)/g, `$1${decimal}`)
}
